// @ts-strict-ignore
import type { Action, PublishedAttribute } from 'flow/store/engine/types';
import { ActionType } from '../../store/engine/constants';
import { filterActionsByType, isActionOfType } from '../actions';
import { PublishedAttributeOwnerType } from '~/dashboard/views/settings/published-attributes/types';
import { generatePublicIdHash } from 'flow/store/engine/utils';

export function isPublishedAttribute(action: Action): boolean {
  if (!isActionOfType(action, ActionType.INPUT) && !isActionOfType(action, ActionType.OUTPUT)) {
    return false;
  }

  const dataObjectKeyName = isActionOfType(action, ActionType.INPUT)
    ? 'input'
    : 'output';


  return Boolean(action[dataObjectKeyName].publishedAttributeToken);
}

export function getPATokenFromAttributeAction(action: Action) {
  if (!isPublishedAttribute(action)) {
    return null;
  }

  return isActionOfType(action, ActionType.INPUT)
    ? action.input.publishedAttributeToken
    : action.output.publishedAttributeToken;
}

export function generateTemporaryPublishedAttributeToken(): string {
  return `TEMP-PA-${generatePublicIdHash(20)}`;
}

/**
 * Creates an ephemeral published attribute with defaults
 */
export function createEphemeralPublishedAttribute({
  attributeName,
  dataType = 'string',
  defaultValue = '',
  ...overrides
}: Partial<PublishedAttribute>): PublishedAttribute {
  return {
    attributeName,
    attributeToken: overrides.attributeToken ?? generateTemporaryPublishedAttributeToken(),
    dataType,
    defaultValue,
    defaultValueNote: '',
    description: '',
    valueExpiresInDays: null,
    containsPii: false,
    jqFilter: undefined,
    archived: false,
    activeWorkflows: [],
    hasExpiration: false,
    hasJqFilter: false,
    ownerType: PublishedAttributeOwnerType.CLIENT,
    objectType: 'entity',
    validValueMin: null,
    validValueMax: null,
    validValues: null,
    agentCanCreateValue: true,
    groupingToken: '',
    isEphemeral: true,
    ...overrides,
  };
}

export function isPublishedAttributeEphemeral(
  publishedAttribute: PublishedAttribute,
): boolean {
  if (!publishedAttribute) {
    return false;
  }
  return Boolean(publishedAttribute.isEphemeral);
}

/**
 * Type guard to check if an object is a valid PublishedAttribute
 * Validates required fields: attributeToken, attributeName, dataType, defaultValue, and isEphemeral
 */
export function isValidPublishedAttribute(
  obj: unknown,
): obj is PublishedAttribute {
  if (!obj || typeof obj !== 'object') {
    return false;
  }

  const candidate = obj as Record<string, unknown>;

  return (
    typeof candidate.attributeToken === 'string'
    && candidate.attributeToken.length > 0
    && typeof candidate.attributeName === 'string'
    && candidate.attributeName.length > 0
    && typeof candidate.dataType === 'string'
    && candidate.dataType.length > 0
    && (typeof candidate.defaultValue === 'string'
      || typeof candidate.defaultValue === 'number'
      || typeof candidate.defaultValue === 'boolean')
    && typeof candidate.isEphemeral === 'boolean'
  );
}


export function indexPublishedAttributesByName(
  publishedAttributes: PublishedAttribute[],
): Record<PublishedAttribute['attributeName'], PublishedAttribute> {
  return Object.fromEntries(
    publishedAttributes.map(publishedAttribute => (
      [publishedAttribute.attributeName, publishedAttribute]
    )),
  );
}

export function getActionsByPublishedAttributeToken(actions: Record<string, Action>, token: PublishedAttribute['attributeToken']): Action[] {
  return filterActionsByType(actions, [ActionType.INPUT, ActionType.OUTPUT])
    .filter(action => isPublishedAttribute(action)
  && getPATokenFromAttributeAction(action) === token);
}
